import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';

import { Store } from '@ngrx/store';

import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, first, mergeMap, switchMap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';

import {
  selectCurrentToken,
  selectAuthState,
  PLLoginRedirect,
  PLCheckUserStatus,
} from '../store';

@Injectable()
export class PlAuthInterceptor implements HttpInterceptor {
  constructor(private store: Store) {}

  intercept(
    httpRequest: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.getAccessToken().pipe(
      mergeMap((token) => {
        let nextReq = httpRequest;

        if (token) {
          nextReq = this.addTokenToReq(httpRequest, token);
        }

        return next.handle(nextReq).pipe(
          catchError((error) => {
            if (
              error instanceof HttpErrorResponse &&
              error.status === 401 &&
              token
            ) {
              return this.handle401(next, nextReq);
            }
            return throwError(error);
          })
        );
      })
    );
  }

  private isStatusRequest(httpRequest: HttpRequest<any>) {
    return httpRequest.url.includes('/api/v1/status/');
  }

  private handle401(next: HttpHandler, httpRequest: HttpRequest<any>) {
    if (this.isStatusRequest(httpRequest)) {
      this.store.dispatch(PLLoginRedirect({ url: window.location.pathname }));
      return EMPTY;
    }

    return this.handleRefresh(next, httpRequest);
  }

  private handleRefresh(next: HttpHandler, httpRequest: HttpRequest<any>) {
    this.store.dispatch(PLCheckUserStatus({ lastActive: 0 }));

    return this.getAccessToken().pipe(
      mergeMap((newToken) =>
        next.handle(this.addTokenToReq(httpRequest, newToken))
      )
    );
  }

  private addTokenToReq(httpRequest: HttpRequest<any>, token: string) {
    return httpRequest.clone({
      setHeaders: { authorization: `JWT ${token}` },
    });
  }

  private getAccessToken() {
    return this.store.select(selectAuthState).pipe(
      filter((authState) => authState !== 'pending'),
      concatLatestFrom(() => this.store.select(selectCurrentToken)),
      switchMap(([, token]) => of(token)),
      first()
    );
  }
}
